import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import PrefecturaReportsSection from '@/components/PrefecturaReportsSection';
import usePolling from '@/hooks/usePolling';
import LastUpdated from '@/components/LastUpdated';
import firstName from '@/utils/firstName';
import { toast } from 'sonner';
import { 
  Search, Calendar as CalendarIcon, FileText, Trash2, Edit2, 
  RefreshCw, Building2, User, BookOpen, CheckCircle, AlertCircle,
  Plus, X, AlertTriangle, Bell, Users, RotateCcw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrientadorDashboard = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [justifications, setJustifications] = useState([]);
  const [institution, setInstitution] = useState(null);
  
  // Search state
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // New justification dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState(false);
  // Camino B (opcional): justificación por horas de UN día → cubrir solo ciertas clases.
  const [coveredClassIds, setCoveredClassIds] = useState([]);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editingJustification, setEditingJustification] = useState(null);
  const [editDates, setEditDates] = useState([]);
  const [editReason, setEditReason] = useState('');
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('single'); // single, student, all

  // Manual cycle reset (Fase E)
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Citatorios state
  const [citatorios, setCitatorios] = useState([]);
  const [pendingCitatorios, setPendingCitatorios] = useState(0);
  const [showCitatorios, setShowCitatorios] = useState(false);

  // Group justification dialog (uses existing model — creates one record per student)
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [groupsList, setGroupsList] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupReason, setGroupReason] = useState('');
  const [groupDates, setGroupDates] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupClasses, setGroupClasses] = useState([]);
  const [groupCoveredClassIds, setGroupCoveredClassIds] = useState([]);

  const openGroupDialog = async () => {
    setSelectedGroups([]);
    setGroupReason('');
    setGroupDates([]);
    setGroupClasses([]);
    setGroupCoveredClassIds([]);
    setShowGroupDialog(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/orientador/groups`, { headers: { Authorization: `Bearer ${token}` } });
      setGroupsList(r.data || []);
    } catch {}
  };

  // When a group justification narrows to a single day, load the groups' classes
  // so the orientador can optionally pick which ones the hour-range covers.
  useEffect(() => {
    if (!showGroupDialog || groupDates.length !== 1 || selectedGroups.length === 0) {
      setGroupClasses([]);
      setGroupCoveredClassIds([]);
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/orientador/group-classes`, {
          params: { groups: selectedGroups.join(',') },
          headers: { Authorization: `Bearer ${token}` }
        });
        setGroupClasses(r.data || []);
      } catch { setGroupClasses([]); }
    })();
  }, [showGroupDialog, groupDates.length, selectedGroups]);

  const handleCreateGroupJustification = async () => {
    if (selectedGroups.length === 0 || groupDates.length === 0 || !groupReason.trim()) {
      toast.error('Selecciona al menos un grupo, una fecha y escribe el motivo');
      return;
    }
    setCreatingGroup(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { groups: selectedGroups, reason: groupReason, dates: groupDates };
      if (groupDates.length === 1 && groupCoveredClassIds.length > 0) {
        payload.covered_class_ids = groupCoveredClassIds;
      }
      const res = await axios.post(`${API}/orientador/justifications/group`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data?.message || 'Justificación grupal creada');
      setShowGroupDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear justificación grupal');
    } finally {
      setCreatingGroup(false);
    }
  };

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/orientador/justifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJustifications(res.data);
      
      // Fetch citatorios
      const citRes = await axios.get(`${API}/orientador/citatorios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCitatorios(citRes.data || []);
      
      const pendingRes = await axios.get(`${API}/orientador/citatorios/pending-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCitatorios(pendingRes.data.pending || 0);
      
      // Get institution info
      const userRes = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (userRes.data.institution_id) {
        const instRes = await axios.get(`${API}/admin/institutions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const inst = instRes.data.find(i => i.id === userRes.data.institution_id);
        setInstitution(inst);
      }
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Real-time auto-refresh of justifications + citatorios ----
  const refreshRealtime = useCallback(async () => {
    const token = localStorage.getItem('token');
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [jRes, cRes, pRes] = await Promise.all([
        axios.get(`${API}/orientador/justifications`, h),
        axios.get(`${API}/orientador/citatorios`, h),
        axios.get(`${API}/orientador/citatorios/pending-count`, h),
      ]);
      setJustifications(jRes.data);
      setCitatorios(cRes.data || []);
      setPendingCitatorios(pRes.data.pending || 0);
      setLastUpdated(Date.now());
    } catch (e) {
      // Ignore transient poll errors on purpose.
    }
  }, []);

  usePolling(refreshRealtime, { intervalMs: 0 });

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await fetchData(); } finally { setRefreshing(false); }
  };

  const handleSearch = async (overrideName) => {
    const term = (typeof overrideName === 'string' ? overrideName : searchName).trim();
    if (!term) {
      toast.error('Ingresa el nombre del alumno');
      return;
    }
    
    setSearching(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await axios.get(`${API}/orientador/search-student`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { name: term }
      });
      setSearchResults(res.data);
      if (!res.data.found) {
        toast.error('No se encontró ningún alumno con ese nombre');
      }
    } catch (error) {
      toast.error('Error al buscar alumno');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setSelectedDates([]);
    setReason('');
    setCoveredClassIds([]);
    setShowNewDialog(true);
    setSearchResults(null);
    setSearchName('');
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      }
      return [...prev, dateStr].sort();
    });
  };

  const handleEditDateSelect = (date) => {
    if (!date) return;
    const dateStr = date.toISOString().split('T')[0];
    setEditDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      }
      return [...prev, dateStr].sort();
    });
  };

  const handleCreateJustification = async () => {
    if (!selectedStudent || selectedDates.length === 0 || !reason.trim()) {
      toast.error('Completa todos los campos');
      return;
    }
    
    setCreating(true);
    const token = localStorage.getItem('token');
    
    try {
      const payload = {
        student_name: selectedStudent.name,
        reason: reason,
        dates: selectedDates
      };
      if (selectedDates.length === 1 && coveredClassIds.length > 0) {
        payload.covered_class_ids = coveredClassIds;
      }
      await axios.post(`${API}/orientador/justifications`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Justificación creada exitosamente');
      setShowNewDialog(false);
      setSelectedStudent(null);
      setSelectedDates([]);
      setReason('');
      setCoveredClassIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear justificación');
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (justification) => {
    setEditingJustification(justification);
    setEditDates([...justification.dates]);
    setEditReason(justification.reason);
    setEditDialog(true);
  };

  const handleUpdateJustification = async () => {
    if (!editingJustification || editDates.length === 0 || !editReason.trim()) {
      toast.error('Completa todos los campos');
      return;
    }
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(`${API}/orientador/justifications/${editingJustification.id}`, {
        reason: editReason,
        dates: editDates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Justificación actualizada');
      setEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const openDeleteDialog = (target, type) => {
    setDeleteTarget(target);
    setDeleteType(type);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    const token = localStorage.getItem('token');
    
    try {
      if (deleteType === 'single') {
        await axios.delete(`${API}/orientador/justifications/${deleteTarget.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Justificación eliminada');
      } else if (deleteType === 'student') {
        await axios.delete(`${API}/orientador/justifications-by-student/${encodeURIComponent(deleteTarget)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Justificaciones del alumno eliminadas');
      } else if (deleteType === 'all') {
        await axios.delete(`${API}/orientador/all-justifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Todas las justificaciones eliminadas');
      }
      
      setDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const markCitatorioAtendido = async (citatorioId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/orientador/citatorios/${citatorioId}/atender`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Citatorio marcado como atendido');
      fetchData();
    } catch (error) {
      toast.error('Error al marcar citatorio');
    }
  };

  const handleResetCycle = async () => {
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/orientador/reset-cycle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Justificantes y citatorios reiniciados');
      setResetOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error al reiniciar');
    } finally {
      setResetting(false);
    }
  };

  // Group justifications by student
  const groupedByStudent = justifications.reduce((acc, j) => {
    const name = j.student_name;
    if (!acc[name]) {
      acc[name] = [];
    }
    acc[name].push(j);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              {firstName(user?.name) && (
                <p className="text-sm text-slate-500 mb-0.5" data-testid="welcome-greeting">
                  Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span>
                </p>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">
                Panel del Orientador
              </h1>
              <p className="text-sm text-slate-600">Gestión de justificaciones de inasistencias</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <LastUpdated timestamp={lastUpdated} onRefresh={handleRefresh} refreshing={refreshing} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetOpen(true)}
                data-testid="orientador-reset-btn"
                className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Reiniciar ciclo
              </Button>
            </div>
          </div>
          
          {institution && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg shadow-sm">
              <Building2 className="w-5 h-5" />
              <span className="font-medium">{institution.name}</span>
            </div>
          )}
        </div>


        {/* Citatorio Reminder Banner */}
        {pendingCitatorios > 0 && (
          <div className="mb-6 bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center justify-between" data-testid="citatorio-reminder-banner">
            <div className="flex items-center gap-3">
              <div className="bg-rose-100 p-2 rounded-full">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="font-semibold text-rose-800">Tienes {pendingCitatorios} citatorio{pendingCitatorios > 1 ? 's' : ''} sin atender</p>
                <p className="text-sm text-rose-600">Revisa la sección de citatorios para dar seguimiento</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowCitatorios(true)} 
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="go-to-citatorios-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Ver Citatorios
            </Button>
          </div>
        )}

        {/* Citatorios Section */}
        <Card className="p-4 mb-6" data-testid="citatorios-section">
          <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => setShowCitatorios(!showCitatorios)}>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500" />
              Citatorios de Profesores
              {pendingCitatorios > 0 && (
                <span className="ml-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="pending-citatorio-badge">
                  {pendingCitatorios} pendiente{pendingCitatorios > 1 ? 's' : ''}
                </span>
              )}
            </h2>
            <Button variant="ghost" size="sm">
              {showCitatorios ? 'Ocultar' : 'Mostrar'}
            </Button>
          </div>
          
          {showCitatorios && (
            <div className="space-y-3">
              {citatorios.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No hay citatorios registrados</p>
                </div>
              ) : (
                citatorios.map((cit) => (
                  <div 
                    key={cit.id} 
                    className={`border rounded-lg p-4 transition-all ${cit.atendido ? 'bg-green-50 border-green-200' : 'bg-rose-50 border-rose-200'}`}
                    data-testid={`citatorio-item-${cit.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {cit.atendido ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                              <CheckCircle className="w-3 h-3" /> Atendido
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 text-xs font-bold rounded-full">
                              <AlertTriangle className="w-3 h-3" /> Pendiente
                            </span>
                          )}
                          <span className="text-xs text-slate-500">
                            {cit.created_at ? new Date(cit.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">
                          Prof. {cit.teacher_name} - {cit.class_subject}
                        </p>
                        <p className="text-sm text-slate-700 mt-1">
                          <strong>Alumnos:</strong> {(cit.student_names || []).join(', ')}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs text-slate-600">
                          <span><strong>Fecha:</strong> {cit.fecha}</span>
                          <span><strong>Hora:</strong> {cit.hora}</span>
                          <span><strong>Lugar:</strong> {cit.lugar}</span>
                          <span><strong>Motivo:</strong> {cit.motivo}</span>
                        </div>
                      </div>
                      {!cit.atendido && (
                        <Button
                          onClick={() => markCitatorioAtendido(cit.id)}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-white ml-3"
                          data-testid={`mark-atendido-${cit.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Atendido
                        </Button>
                      )}
                    </div>
                    {cit.atendido && cit.atendido_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Atendido el {new Date(cit.atendido_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Reportes de Prefectura (el orientador puede marcar como atendido) */}
        <PrefecturaReportsSection canAttend canDelete />

        {/* Search Section */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Search className="w-5 h-5" />
              Buscar Alumno
            </h2>
            <Button onClick={openGroupDialog} className="bg-orange-500 hover:bg-orange-600" data-testid="group-justify-btn">
              <Users className="w-4 h-4 mr-2" />
              Justificar Grupo
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Nombre o matrícula del alumno (ej: GARCIA LOPEZ JUAN o A-1001)"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e.target.value)}
              className="flex-1"
              data-testid="search-student-input"
            />
            <Button onClick={handleSearch} disabled={searching} data-testid="search-btn">
              {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </Button>
          </div>
          
          {/* Search Results */}
          {searchResults && searchResults.found && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-slate-600">Resultados encontrados:</p>
              {searchResults.students.map((student, idx) => (
                <Card key={idx} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => handleSelectStudent(student)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {student.name}
                      </p>
                      {student.matricula && (
                        <p className="text-xs text-slate-500 mt-0.5">Matrícula: {student.matricula}</p>
                      )}
                      <div className="text-xs text-slate-500 mt-1">
                        {student.classes.map((c, i) => (
                          <span key={i} className="inline-block mr-2">
                            <BookOpen className="w-3 h-3 inline mr-1" />
                            {c.subject} ({c.group})
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Justificar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-purple-600">{justifications.length}</div>
            <div className="text-sm text-slate-600">Justificaciones</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{Object.keys(groupedByStudent).length}</div>
            <div className="text-sm text-slate-600">Alumnos</div>
          </Card>
          <Card className="p-4 text-center col-span-2">
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => openDeleteDialog(null, 'all')}
              disabled={justifications.length === 0}
              className="w-full"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar Todo
            </Button>
          </Card>
        </div>

        {/* Justifications List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Justificaciones Registradas
          </h2>
          
          {justifications.length === 0 ? (
            <Card className="p-8 text-center text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay justificaciones registradas</p>
              <p className="text-sm">Busca un alumno para crear una justificación</p>
            </Card>
          ) : (
            Object.entries(groupedByStudent).map(([studentName, studentJustifications]) => (
              <Card key={studentName} className="overflow-hidden">
                <div className="bg-slate-100 p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold">{studentName}</span>
                    <span className="text-sm text-slate-500">({studentJustifications.length} justificaciones)</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500"
                    onClick={() => openDeleteDialog(studentName, 'student')}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar del alumno
                  </Button>
                </div>
                <div className="divide-y">
                  {studentJustifications.map(j => (
                    <div key={j.id} className="p-3 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">Motivo: {j.reason}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {j.dates.map(d => (
                              <span key={d} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                {formatDate(d)}
                              </span>
                            ))}
                          </div>
                          {Array.isArray(j.covered_class_ids) && j.covered_class_ids.length > 0 ? (
                            <>
                              <p className="text-xs text-blue-600 mt-2 font-medium">⏱ Justificación por horas (solo ciertas clases)</p>
                              <p className="text-xs text-slate-400 mt-0.5">
                                Clases cubiertas: {j.affected_classes.filter(c => j.covered_class_ids.includes(c.class_id)).map(c => c.subject).join(', ')}
                              </p>
                            </>
                          ) : (
                            <p className="text-xs text-slate-400 mt-2">
                              Clases afectadas: {j.affected_classes.map(c => c.subject).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(j)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => openDeleteDialog(j, 'single')}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))
          )}
        </div>

        {/* New Justification Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva Justificación</DialogTitle>
            </DialogHeader>
            
            {selectedStudent && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {selectedStudent.name}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Clases: {selectedStudent.classes.map(c => `${c.subject} (${c.group})`).join(', ')}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Motivo de la justificación</label>
                  <Input
                    placeholder="Ej: Cita médica, Enfermedad, etc."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="reason-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Fechas a justificar ({selectedDates.length} seleccionadas)
                  </label>
                  <div className="flex flex-wrap gap-1 mb-2 min-h-[30px]">
                    {selectedDates.map(d => (
                      <span key={d} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1">
                        {formatDate(d)}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedDates(prev => prev.filter(x => x !== d))} />
                      </span>
                    ))}
                  </div>
                  <Calendar
                    mode="multiple"
                    selected={selectedDates.map(d => new Date(d + 'T00:00:00'))}
                    onSelect={(dates) => {
                      if (dates) {
                        setSelectedDates(dates.map(d => d.toISOString().split('T')[0]).sort());
                      }
                    }}
                    className="rounded-md border"
                  />
                </div>
                
                {selectedDates.length === 1 && selectedStudent.classes?.length > 0 && (
                  <div className="border border-purple-200 rounded-lg p-3 bg-purple-50/50" data-testid="covered-classes-individual">
                    <label className="block text-sm font-medium mb-1">
                      Justificar solo ciertas clases <span className="text-slate-400 font-normal">(opcional)</span>
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                      Úsalo si el alumno solo está justificado unas horas (ej. 7-9am): marca las clases cubiertas. Si no marcas ninguna, se justifica el <b>día completo</b> como siempre.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedStudent.classes.map((c) => {
                        const checked = coveredClassIds.includes(c.class_id);
                        return (
                          <button
                            key={c.class_id}
                            type="button"
                            onClick={() => setCoveredClassIds(prev => checked ? prev.filter(x => x !== c.class_id) : [...prev, c.class_id])}
                            className={`px-3 py-1 text-xs rounded-full border ${checked ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-700 border-slate-300 hover:border-purple-400'}`}
                            data-testid={`covered-class-${c.class_id}`}
                          >
                            {c.subject} ({c.group})
                          </button>
                        );
                      })}
                    </div>
                    {coveredClassIds.length > 0 && (
                      <p className="text-xs text-purple-700 mt-2">Solo estas clases marcarán JO ese día; las demás quedan editables.</p>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowNewDialog(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateJustification} disabled={creating} className="flex-1 bg-purple-600 hover:bg-purple-700">
                    {creating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Crear Justificación
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Group Justification Dialog */}
        <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-justify-dialog">
            <DialogHeader>
              <DialogTitle>Justificación Grupal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-orange-50 rounded-lg text-xs text-orange-700">
                Esta acción creará una justificación para <b>todos los alumnos</b> de los grupos seleccionados. Cada alumno tendrá la marca JO visible para sus profesores en las fechas indicadas.
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Grupos ({selectedGroups.length} seleccionados)</label>
                <div className="flex flex-wrap gap-2 border rounded p-2 max-h-40 overflow-y-auto">
                  {groupsList.length === 0 ? (
                    <p className="text-xs text-slate-400">Cargando grupos...</p>
                  ) : groupsList.map(g => {
                    const checked = selectedGroups.includes(g);
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setSelectedGroups(prev => checked ? prev.filter(x => x !== g) : [...prev, g])}
                        className={`px-3 py-1 text-xs rounded-full border ${checked ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 border-slate-300 hover:border-orange-400'}`}
                        data-testid={`group-chip-${g}`}
                      >
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Motivo</label>
                <Input
                  placeholder="Ej: Visita educativa, suspensión por evento, etc."
                  value={groupReason}
                  onChange={(e) => setGroupReason(e.target.value)}
                  data-testid="group-reason-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fechas a justificar ({groupDates.length})</label>
                <div className="flex flex-wrap gap-1 mb-2 min-h-[30px]">
                  {groupDates.map(d => (
                    <span key={d} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded flex items-center gap-1">
                      {formatDate(d)}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setGroupDates(prev => prev.filter(x => x !== d))} />
                    </span>
                  ))}
                </div>
                <Calendar
                  mode="multiple"
                  selected={groupDates.map(d => new Date(d + 'T00:00:00'))}
                  onSelect={(dates) => {
                    if (dates) setGroupDates(dates.map(d => d.toISOString().split('T')[0]).sort());
                  }}
                  className="rounded-md border"
                />
              </div>
              {groupDates.length === 1 && groupClasses.length > 0 && (
                <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50" data-testid="covered-classes-group">
                  <label className="block text-sm font-medium mb-1">
                    Justificar solo ciertas clases <span className="text-slate-400 font-normal">(opcional)</span>
                  </label>
                  <p className="text-xs text-slate-500 mb-2">
                    Para justificaciones de unas horas (ej. 7-9am): marca las clases cubiertas. Si no marcas ninguna, se justifica el <b>día completo</b> como siempre.
                  </p>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                    {groupClasses.map((c) => {
                      const checked = groupCoveredClassIds.includes(c.class_id);
                      return (
                        <button
                          key={c.class_id}
                          type="button"
                          onClick={() => setGroupCoveredClassIds(prev => checked ? prev.filter(x => x !== c.class_id) : [...prev, c.class_id])}
                          className={`px-3 py-1 text-xs rounded-full border ${checked ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 border-slate-300 hover:border-orange-400'}`}
                          data-testid={`covered-group-class-${c.class_id}`}
                        >
                          {c.subject} ({c.group})
                        </button>
                      );
                    })}
                  </div>
                  {groupCoveredClassIds.length > 0 && (
                    <p className="text-xs text-orange-700 mt-2">Solo estas clases marcarán JO ese día; las demás quedan editables.</p>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowGroupDialog(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateGroupJustification}
                  disabled={creatingGroup || selectedGroups.length === 0 || groupDates.length === 0 || !groupReason.trim()}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
                  data-testid="group-justify-submit"
                >
                  {creatingGroup ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Crear Justificación Grupal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Justificación</DialogTitle>
            </DialogHeader>
            
            {editingJustification && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="font-medium">{editingJustification.student_name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Motivo</label>
                  <Input
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Fechas ({editDates.length} seleccionadas)
                  </label>
                  <div className="flex flex-wrap gap-1 mb-2 min-h-[30px]">
                    {editDates.map(d => (
                      <span key={d} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1">
                        {formatDate(d)}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => setEditDates(prev => prev.filter(x => x !== d))} />
                      </span>
                    ))}
                  </div>
                  <Calendar
                    mode="multiple"
                    selected={editDates.map(d => new Date(d + 'T00:00:00'))}
                    onSelect={(dates) => {
                      if (dates) {
                        setEditDates(dates.map(d => d.toISOString().split('T')[0]).sort());
                      }
                    }}
                    className="rounded-md border"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setEditDialog(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button onClick={handleUpdateJustification} className="flex-1 bg-purple-600 hover:bg-purple-700">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminación</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {deleteType === 'single' && (
                <p>¿Estás seguro de eliminar esta justificación?</p>
              )}
              {deleteType === 'student' && (
                <p>¿Estás seguro de eliminar todas las justificaciones de <strong>{deleteTarget}</strong>?</p>
              )}
              {deleteType === 'all' && (
                <p className="text-red-600 font-medium">¿Estás seguro de eliminar TODAS las justificaciones? Esta acción no se puede deshacer.</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeleteDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete} className="flex-1">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Cycle Confirmation Dialog (Fase E) */}
        <Dialog open={resetOpen} onOpenChange={setResetOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Reiniciar ciclo
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 text-sm text-slate-600">
              <p>Se eliminarán <strong>todos los justificantes y citatorios</strong> de tu institución. Esta acción no se puede deshacer.</p>
              <p className="mt-2">El <strong>Historial de Conducta</strong> se conserva y no se verá afectado.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setResetOpen(false)} className="flex-1" data-testid="orientador-reset-cancel">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleResetCycle} disabled={resetting} className="flex-1" data-testid="orientador-reset-confirm">
                <RotateCcw className="w-4 h-4 mr-2" />
                {resetting ? 'Reiniciando...' : 'Reiniciar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default OrientadorDashboard;
